class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content =
      this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      'focusout',
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 100);
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class DetailsDisclosureBase extends HTMLElement {
  constructor(focusOut = true) {
    super();
    this.name = this.dataset.name ?? 'disclosure';
    this.containerClasses = [`has-open-${this.name}`];
    this.focusOut = focusOut;
    this.onSummaryClick = this.handleSummaryClick.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeyUp = this.handleKeyup.bind(this);

    this.disclosureContainer = this.closest('[data-disclosure-container]');
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.querySelector('summary');
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this.summaryElement.addEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.addEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.addEventListener('keyup', this.onKeyUp);
  }

  unbindEvents() {
    this.summaryElement.removeEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.removeEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.removeEventListener('keyup', this.onKeyUp);
  }

  isOpen() {
    return this.detailsElement.hasAttribute('open');
  }

  handleSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  handleFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  handleKeyup(event) {
    event.code.toUpperCase() === 'ESCAPE' && this.close();
  }

  open() {
    this.detailsElement.setAttribute('open', true);
    this.disclosureContainer.classList.add(...this.containerClasses);
  }

  close() {
    this.detailsElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', false);
    this.disclosureContainer.classList.remove(...this.containerClasses);
  }
}

class MenuDisclosure extends DetailsDisclosureBase {
  constructor() {
    super();

    this.item = this.closest('li');
    this.child = this.querySelector('summary + div');
    this.innerChild = this.child.firstElementChild;
    this.siblings = Array.from(
      this.disclosureContainer.querySelectorAll('menu-disclosure'),
    ).filter(sibling => sibling !== this);
    this.allChildren = Array.from(
      this.disclosureContainer.querySelectorAll(
        'menu-disclosure summary + div',
      ),
    );
    this.background = this.disclosureContainer.querySelector(
      '[data-menu-background]',
    );

    this.header = document.querySelector('.header-section');
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.rootLinks = this.disclosureContainer.querySelectorAll(
      '.header__menu-item--link, .header__icon',
    );

    this.timeline = gsap.timeline({ paused: true });
    this.openTimeline = gsap.timeline({ paused: true });
    this.switchTimeline = gsap.timeline({
      paused: true,
      onComplete: () =>
        this.disclosureContainer.classList.remove('is-switching'),
    });
    this.onMouseOver = this.handleMouseOver.bind(this);
    this.onClose = this.handleClose.bind(this);
    this.onRootLinkHover = this.handleRootLinkHover.bind(this);
    this.onResize = this.handleResize.bind(this);
    this.hoverIntentListeners = null;

    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    this.getHeaderHeight();
    this.buildTimelines();
  }

  bindEvents() {
    super.bindEvents();

    if (typeof window.hoverintent !== 'undefined') {
      const main = hoverintent(this, this.onMouseOver, () => {});
      main.options({ interval: 50 });

      const rootLinks = [];
      this.rootLinks.forEach(link => {
        const rootLinkListener = hoverintent(
          link,
          this.onRootLinkHover,
          () => {},
        );
        rootLinkListener.options({ interval: 50 });
        rootLinks.push(rootLinkListener);
      });

      this.hoverIntentListeners = { main, rootLinks };
    } else {
      this.addEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link =>
        link.addEventListener('mouseenter', this.onRootLinkHover),
      );
    }

    this.header.addEventListener('mouseleave', this.onClose);

    // this.addEventListener('submenu-active', () => {
    //   this.updateHeight();
    // });

    this.isSwitching = false;
  }

  unbindEvents() {
    super.unbindEvents();

    if (this.hoverIntentListeners) {
      this.hoverIntentListeners.main.remove();
      this.hoverIntentListeners.rootLinks.forEach(listener =>
        listener.remove(),
      );
      this.hoverIntentListeners = null;
    } else {
      this.removeEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link =>
        link.removeEventListener('mouseenter', this.onRootLinkHover),
      );
    }

    this.header.removeEventListener('mouseleave', this.onClose);
  }

  handleSummaryClick(e) {
    const innerLink = e.currentTarget.querySelector('a');
    if (innerLink) return;

    e.preventDefault();

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  handleResize() {
    if (this.isOpen()) {
      this.updateHeight();
    }
  }

  getSubnavHeight() {
    return this.child.offsetHeight;
  }

  updateHeight() {
    gsap.set(this.background, {
      y: this.getSubnavHeight.bind(this),
    });
  }

  onlyWhenMotionEnabled(amount) {
    return this.prefersReducedMotion.matches ? 0 : amount;
  }

  buildTimeline(timeline, switching) {
    // TODO: Consider auto-updating subnav height on window resize
    timeline
      .to(this.background, {
        y: this.getSubnavHeight.bind(this),
        duration: this.onlyWhenMotionEnabled(switching ? 0.35 : 0.5),
        ease: 'power2.inOut',
      })
      .to(
        this.allChildren,
        {
          opacity: 0,
          duration: this.onlyWhenMotionEnabled(0.35),
          ease: 'power2.out',
        },
        0,
      )
      .fromTo(
        this.child,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: this.onlyWhenMotionEnabled(0.35),
          ease: switching ? 'power2.in' : 'power2.inOut',
        },
        this.onlyWhenMotionEnabled(switching ? 0 : 0.2),
      );

    if (!switching) {
      timeline.fromTo(
        this.innerChild,
        {
          y: -60,
        },
        {
          y: 0,
          duration: this.onlyWhenMotionEnabled(0.5),
          ease: 'power2.inOut',
          onComplete: () => gsap.set(this.innerChild, { clearProps: 'y' }),
        },
        this.onlyWhenMotionEnabled(0.05),
      );
    } else {
      gsap.set(this.innerChild, { clearProps: 'y' });
    }
  }

  hideSiblings() {
    this.allChildren
      .filter(child => child !== this.child)
      .forEach(child => gsap.set(child, { opacity: 0 }));
  }

  buildTimelines() {
    this.buildTimeline(this.openTimeline, false);
    this.buildTimeline(this.switchTimeline, true);
  }

  isOpen() {
    return this.item.classList.contains('is-open');
  }

  handleMouseOver() {
    this.open();
  }

  handleClose() {
    this.cancelClose();

    this.closingTime = setTimeout(() => {
      this.close();
    }, 150);
  }

  handleRootLinkHover() {
    if (this.isOpen()) {
      this.handleClose();
    }
  }

  getHeaderHeight() {
    if (
      document.documentElement.style.getPropertyValue(
        '--header-bottom-position-desktop',
      ) === ''
    ) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`,
      );
      document.documentElement.style.setProperty(
        '--header-height',
        `${Math.floor(this.header.offsetHeight)}px`,
      );
    }
  }

  open() {
    this.getHeaderHeight();

    this.isSwitching = this.hasOpenSibling();

    this.cancelClose();
    this.closeSiblings();

    if (this.isOpen()) return;

    /** @type {HeaderSubmenu|null} */
    const submenu = this.querySelector('header-submenu');

    if (submenu) {
      submenu.setFirstMenu();
    }

    super.open();
    this.summaryElement?.setAttribute('aria-expanded', true);

    if (this.isSwitching) {
      this.disclosureContainer.classList.add('is-switching');
    }

    this.item.classList.add('is-open');
    this.headerWrapper.preventHide = true;

    window.addEventListener('resize', this.onResize);

    setTimeout(() => {
      this.disclosureContainer.classList.add('has-menu-buffer');

      if (this.isSwitching) {
        this.switchTimeline.invalidate().play(0);
      } else {
        this.openTimeline.invalidate().play(0);
      }
    });
  }

  hasOpenSibling() {
    return this.siblings.some(sibling => sibling.isOpen());
  }

  cancelClose() {
    clearTimeout(this.closingTime);
    this.closingTime = undefined;
  }

  closeSiblings() {
    this.siblings.forEach(sibling => sibling.close());
  }

  close() {
    this.cancelClose();

    if (!this.isOpen()) return;

    this.headerWrapper.preventHide = false;
    this.item.classList.remove('is-open');
    this.summaryElement.setAttribute('aria-expanded', false);

    window.removeEventListener('resize', this.onResize);

    setTimeout(() => {
      if (!this.hasOpenSibling()) {
        this.disclosureContainer.classList.remove(...this.containerClasses);
        this.timeline.pause();
        gsap.to(this.innerChild, {
          duration: this.onlyWhenMotionEnabled(0.5),
          y: -60,
          ease: 'power2.inOut',
          onComplete: () => gsap.set(this.innerChild, { clearProps: 'y' }),
        });
        gsap.to(this.child, {
          opacity: 0,
          duration: this.onlyWhenMotionEnabled(0.4),
          ease: 'power2.inOut',
        });

        gsap.to(this.background, {
          y: 0,
          duration: this.onlyWhenMotionEnabled(0.5),
          ease: 'power2.inOut',
          delay: this.onlyWhenMotionEnabled(0.05),
        });
      }
    });

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = time => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else if (!this.isOpen()) {
        this.detailsElement.removeAttribute('open');

        if (!this.hasOpenSibling()) {
          this.disclosureContainer.classList.remove('has-menu-buffer');
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-disclosure', MenuDisclosure);

class HeaderSubmenu extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();

    /** @type {NodeListOf<HTMLElement>} */
    this.submenuToggles = this.querySelectorAll('.submenu-menu-toggle');

    /** @type {NodeListOf<HTMLElement>} */
    this.submenuButtons = this.querySelectorAll(
      '.submenu-menu-toggle a,.submenu-menu-toggle button',
    );

    /** @type {NodeListOf<HTMLElement>} */
    this.submenuItems = this.querySelectorAll('.header__submenu-item');

    this.handleSubmenuMouseEnter = this.handleSubmenuMouseEnter.bind(this);
    this.handleSubmenuMouseLeave = this.handleSubmenuMouseLeave.bind(this);
    this.handleSubmenuKeyDown = this.handleSubmenuKeyDown.bind(this);
    this.handleSubmenuFocus = this.handleSubmenuFocus.bind(this);

    this.handleSubmenuItemKeyDown = this.handleSubmenuItemKeyDown.bind(this);

    /** @type {HTMLElement|null} */
    this.activeElement = null;

    /** @type {NodeJS.Timeout|null} */
    this.timeoutID = null;
  }

  connectedCallback() {
    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('mouseenter', this.handleSubmenuMouseEnter);
      toggle.addEventListener('mouseleave', this.handleSubmenuMouseLeave);
    });

    this.submenuButtons.forEach(button => {
      button.addEventListener('keydown', this.handleSubmenuKeyDown);
      button.addEventListener('focus', this.handleSubmenuFocus);
    });

    this.submenuItems.forEach(item => {
      item.addEventListener('keydown', this.handleSubmenuItemKeyDown);
    });
  }

  setNextMenu() {
    if (this.activeElement === null) {
      return;
    }

    /** @type {HTMLElement|null} */
    let nextToggle = null;

    for (let i = 0; i < this.submenuToggles.length; i++) {
      if (this.submenuToggles[i].dataset.state === 'active') {
        nextToggle = this.submenuToggles[i + 1] || null;
        break;
      }
    }

    if (nextToggle) {
      /** @type {HTMLElement} */
      // @ts-ignore
      this.activeElement =
        nextToggle.tagName === 'A'
          ? nextToggle
          : nextToggle.querySelector('a,button');

      this.activeElement?.focus();
      this.setActiveMenuFromToggle(nextToggle);
    }
  }

  /** @param {KeyboardEvent} event */
  handleSubmenuItemKeyDown(event) {
    if (event.key !== 'Tab') {
      return;
    }
    /** @type {HTMLElement} */
    // @ts-ignore
    const target = event.target;

    /** @type {HTMLElement} */
    // @ts-ignore
    const container = target.closest('.header__submenu-items');
    const links = container.querySelectorAll('a');

    if (event.shiftKey === false && links[links.length - 1] === target) {
      this.setNextMenu();
      event.preventDefault();
      return;
    }

    if (event.shiftKey === true && links[0] === target) {
      this.activeElement?.focus();
      event.preventDefault();
      return;
    }
  }

  /** @param {Event} event */
  handleSubmenuFocus(event) {
    /** @type {HTMLElement} */
    // @ts-ignore
    const parent = event.target.parentElement;
    const menuNumber = Number(parent.dataset.submenu);
    this.setActiveMenu(parent, menuNumber);
  }

  /**
   * @param {HTMLElement} toggle
   */
  setActiveMenuFromToggle(toggle) {
    const menuNumber = Number(toggle.dataset.submenu);
    this.setActiveMenu(toggle, menuNumber);
  }

  /** @param {KeyboardEvent} event */
  handleSubmenuKeyDown(event) {
    const key = event.key;

    /** @type {HTMLElement} */
    // @ts-ignore
    const target = event.target;

    if (key !== 'Tab') {
      return;
    }

    if (key === 'Tab' && event.shiftKey) {
      return;
    }

    this.activeElement = target;

    /** @type {HTMLElement|null} */
    const firstLink = this.querySelector(
      '.header__submenu-items.is-expanded a',
    );

    if (firstLink) {
      firstLink.focus();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * @param {Event} event
   */
  handleSubmenuMouseEnter(event) {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
      this.timeoutID = null;
    }

    /** @type {HTMLElement} */
    // @ts-ignore
    const target = event.currentTarget;
    const menuNumber = Number(target.dataset.submenu);

    this.timeoutID = setTimeout(() => {
      this.setActiveMenu(target, menuNumber);
    }, 180);
  }

  setFirstMenu() {
    const btn = this.submenuToggles[0];
    this.setActiveMenu(btn, 1);
  }

  /**
   * @param {Event} event
   */
  handleSubmenuMouseLeave(event) {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
      this.timeoutID = null;
    }
  }

  /**
   * @param {HTMLElement} toggleBtn
   * @param {number} menuNumber
   */
  setActiveMenu(toggleBtn, menuNumber) {
    this.submenuToggles.forEach(toggle => {
      if (toggle === toggleBtn) {
        toggle.setAttribute('data-state', 'active');
        toggle
          .querySelector('.header__submenu-title')
          ?.setAttribute('aria-expanded', 'true');
      } else {
        toggle.setAttribute('data-state', 'inactive');
        toggle
          .querySelector('.header__submenu-title')
          ?.setAttribute('aria-expanded', 'false');
      }
    });

    // Some "toggles" are just links, we want the same hover/etc behaviour but to keep
    // the same menu open.
    /** @type {NodeListOf<HTMLElement>} */
    const subMenus = this.querySelectorAll('.header__submenu-items');

    subMenus.forEach((subMenu, index) => {
      if (index + 1 === menuNumber) {
        subMenu.style.display = 'grid';
        window.setTimeout(() => {
          subMenu.classList.add('is-expanded');
        }, 10);
      } else {
        subMenu.style.display = '';
        subMenu.classList.remove('is-expanded');
      }
    });

    this.dispatchEvent(
      new CustomEvent('submenu-active', { detail: menuNumber, bubbles: true }),
    );
  }
}

customElements.define('header-submenu', HeaderSubmenu);
